<template>
  <div name="presentation-color-palette-tab" class="pt-14" v-aha-emit-action.view>
    <div class="p-4">
      <div v-if="canUseTheme" class="form-group mb-4">
        <label class="label-option flex mb-4 font-bold">{{ $i18nPresenter.t('Themes') }}</label>
        <div class="theme-list">
          <div
            class="theme-item"
            v-for="(item, index) in themes"
            :key="index"
            :class="{active: item.themeActive}"
          >
            <div
              class="theme-content horizontal layout center center-justified"
              :style="{
                'background-image': 'url(' + item.themeThumbnail + ')',
              }"
              @click="setTheme(item)"
              v-hover-intent="{
                onEnter: () => previewTheme(item),
                onExit: () => removePreview(item)
              }"
            >
            </div>
          </div>
        </div>
      </div>
      <div class="mb-4">
        <span class="font-bold"> {{ $i18nPresenter.t('Chart colours') }}</span>
        <i
          name="presentation-color-palette"
          class="fas fa-question-circle ml-2 cursor-pointer"
        ></i>

        <span class="text-pink-50 font-bold bg-pink-10 rounded px-2 py-1 ml-2">Beta</span>
      </div>
      <aha-toggle-tip
        to="presentation-color-palette"
        :text="tooltipContent"
      >
      </aha-toggle-tip>

      <div class="grid grid-cols-2 gap-4">
        <button
          type="button"
          v-for="(item, index) in presetColorPalettes"
          :key="index"
          class="item-color-palette"
          :class="{
            'active': (presentationAttributeColorPalette && item.title === presentationAttributeColorPalette.title) ||
              (!presentationAttributeColorPalette && !index)
          }"
          name="chart-color-palette"
          v-aha-emit-action="{colorPalette: item.id}"
          @click="selectColorPalette(item)"
        >
          <ColorPalleteThumbnail
            class="color-pallete-thumbnail"
            :colors="item.colors"
          >
          </ColorPalleteThumbnail>
          <div class="mt-2">
            {{ item.title }}
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import ColorPalleteThumbnail from '@/components-v2/presentation-editor/side-right/right-nav/ColorPalleteThumbnail.vue'
import DesignEditPanel from '@/components/presentations/types/DesignEditPanel.vue'

export default {
  name: 'ColorPaletteConfig',
  components: {
    ColorPalleteThumbnail,
  },
  extends: DesignEditPanel,
  computed: {
    ...mapGetters('presetModule', ['presetColorPalettes']),
    ...mapGetters('presentationAttributeModule', ['presentationAttributeColorPalette']),
    ...mapGetters('themeModule', ['canUseTheme']),
    ...mapGetters('history', ['canUseUndoRedoV2']),
    ...mapState({
      presentation: (state) => state.presentation,
      slideActive: (state) => state.slideActive,
    }),
    tooltipContent() {
      return this.$i18nPresenter.t('Chart colours apply to all quiz, poll, word cloud, open-ended, brainstorm, and scale slides.')
    },
  },
  created() {
    this.getPresetColorPalettesAction()
  },
  methods: {
    ...mapActions(['updatePresentationByIdAction', 'updateSlideActiveAction']),
    ...mapActions('presetModule', ['getPresetColorPalettesAction']),
    ...mapActions('presentationAttributeModule', ['updatePresentationAttributeAction']),
    ...mapActions('themeModule', ['setThemeAction']),
    ...mapActions('history', ['setSuppressHistoryAction']),
    async setTheme(item) {
      if (this.canUseUndoRedoV2) {
        this.setSuppressHistoryAction(true)
      }
      const allSlides = this.presentation.slides
      this.selectTheme(item, allSlides)
      this.setThemeAction({
        presentationId: this.presentation.id,
        ...item,
      })
      this.setSuppressHistoryAction(false)
    },
    async previewTheme(item) {
      const allSlides = this.presentation.slides
      this.handleHoverOnTheme(item, 'over', allSlides)
    },
    async removePreview(item) {
      const allSlides = this.presentation.slides
      this.handleHoverOnTheme(item, 'out', allSlides)
    },
    async selectColorPalette(item) {
      await this.updatePresentationAttributeAction({
        presentationId: this.presentation.id,
        payload: {
          attributeName: 'colorPalette',
          attributeValue: item,
        },
      })
      const slideActive = JSON.parse(JSON.stringify(this.slideActive))
      if (this.canUseUndoRedoV2) {
        this.setSuppressHistoryAction(true)
      }
      this.updateSlideActiveAction({ type: 'freeStyle' })
      await this.$nextTick()
      this.updateSlideActiveAction({
        type: slideActive.type,
      })
      this.setSuppressHistoryAction(false)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.fa-question-circle {
  color: #b7b7b7;
}

.item-color-palette {
  @apply inline-block cursor-pointer text-left;
  vertical-align: top;
  .color-pallete-thumbnail {
    max-width: 100%;
    @apply border-base-30;
    border-width: 1px;
    border-style: solid;
    border-radius: 6px;
  }

  &.active {
    @apply text-primary-90;
    .color-pallete-thumbnail {
      @apply border-primary-90 border-2;
    }
  }

  &:hover {
    .color-pallete-thumbnail {
      @apply border-primary-80;
    }
  }

  &:focus-visible {
    @apply outline-none;
    & .color-pallete-thumbnail {
      @apply border-primary-80 border-2;
    }
  }
}

.theme-list {
  margin-left: -4px;
  margin-right: -4px;

  .theme-content {
    width: 104px;
    height: 65px;
    padding: 10px;
    text-align: center;
    vertical-align: middle;
    border-radius: 0.25rem;
    cursor: pointer;
    position: relative;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    overflow: hidden;
    transition: .5s;
    transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);
  }

  .theme-name {
    text-align: center;
    font-size: 12px;
  }

  .theme-item {
    display: inline-block;
    margin-bottom: 4px;
    padding-left: 4px;
    padding-right: 4px;

    &:hover {
      .theme-content {
        @apply shadow-lg;
      }
    }

    &.active, &.active:hover {
      .theme-content {
        @apply shadow-lg;
      }

      .theme-name {
        color: $primary-color;
      }
    }
  }
}
</style>
